<template lang="pug">
.pulse-config
  .config-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.title') }}
  .total-metrics-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.all_segments_metrics') }}
      .metric-count {{ localConfig.total_metrics.length }} / {{ maxMetrics }}
    PulseMetricPicker(
      :metrics="localConfig.total_metrics"
      :availableMetrics="availableMetrics"
      :maxMetrics="maxMetrics"
      @update="onTotalMetricsUpdate"
    )

  PulseConfigSegment(
    v-for="(segment, index) in localConfig.segments"
    :key="index"
    :segment="segment"
    :index="index"
    :availableSegments="availableSegmentsByIndex[index]"
    :availableMetrics="availableMetrics"
    :maxMetrics="maxMetrics"
    @update="onSegmentUpdate"
  )

  .no-interaction-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.no_interaction.title') }}
        aiq-tooltip(placement="top" :content="$t('settings_tab.agent_tab.dashboard.pulse_configuration.no_interaction.tooltip')")
          fa-icon.section-info(:icon="['fas', 'circle-question']")
      aiq-switch(
        active-text=""
        inactive-text=""
        v-model="localConfig.include_no_interaction"
      )

  .cost-config-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.title') }}
        aiq-tooltip(placement="top" :content="$t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.tooltip')")
          fa-icon.section-info(:icon="['fas', 'circle-question']")
    .cost-fields
      .cost-field
        span.cost-field-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.phone_call_cost') }}
        .cost-input-row
          span.currency-symbol $
          aiq-input-number(
            v-model="localConfig.cost_config.phone_call_cost"
            size="small"
            :min="0"
            :precision="2"
            :controls="false"
          )
      .cost-field
        span.cost-field-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.agent_chat_cost') }}
        .cost-input-row
          span.currency-symbol $
          aiq-input-number(
            v-model="localConfig.cost_config.agent_chat_cost"
            size="small"
            :min="0"
            :precision="2"
            :controls="false"
          )
      .cost-field
        span.cost-field-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.automated_conversation_cost') }}
        .cost-input-row
          span.currency-symbol $
          aiq-input-number(
            v-model="localConfig.cost_config.automated_conversation_cost"
            size="small"
            :min="0"
            :precision="2"
            :controls="false"
          )

  .sales-lead-tags-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.title') }}
        aiq-tooltip(placement="top" :content="$t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.tooltip')")
          fa-icon.section-info(:icon="['fas', 'circle-question']")
    aiq-select(
      v-model="localConfig.sales_lead_tags"
      multiple
      filterable
      :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.placeholder')"
      popper-class="pulse-config-dropdown"
    )
      aiq-option(
        v-for="tagName in availableTags"
        :key="tagName"
        :label="tagName"
        :value="tagName"
      )

  .action-buttons
    aiq-button(@click="onCancel") {{ $t('global.cancel') }}
    aiq-button(@click="onSave" :disabled="!isDataChanged" type="primary") {{ $t('global.save') }}
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import difference from 'lodash/difference';
import times from 'lodash/times';
import PulseConfigSegment from './components/PulseConfigSegment.vue';
import PulseMetricPicker from './components/PulseMetricPicker.vue';
import {
  DEFAULT_COST_CONFIG,
  METRIC_IDS,
  MAX_METRICS_PER_SECTION,
  MAX_SEGMENT_SLOTS,
} from '@/pages/Pulse/365/constants';

const DEFAULT_CONFIG = {
  total_metrics: [],
  segments: times(MAX_SEGMENT_SLOTS, () => ({ segment_names: [], metrics: [] })),
  cost_config: { ...DEFAULT_COST_CONFIG },
  sales_lead_tags: [],
  include_no_interaction: true,
};

function mergeWithDefaults(config) {
  const savedCostConfig = get(config, 'cost_config', {});
  const costConfig = { ...DEFAULT_CONFIG.cost_config };

  Object.keys(costConfig).forEach(key => {
    if (!isNil(savedCostConfig[key])) {
      costConfig[key] = savedCostConfig[key];
    }
  });

  return {
    ...DEFAULT_CONFIG,
    ...config,
    cost_config: costConfig,
    sales_lead_tags: [...(config?.sales_lead_tags || [])],
    // Anything but an explicit false reads as on, so a config saved before the
    // toggle existed shows the same state onSave would write back for it.
    include_no_interaction: get(config, 'include_no_interaction') !== false,
  };
}

export default {
  name: 'PulseConfig',
  components: {
    PulseConfigSegment,
    PulseMetricPicker,
  },
  props: {
    config: {
      type: Object,
      default: () => cloneDeep(DEFAULT_CONFIG),
    },
    segments: {
      type: Array,
      default: () => [],
    },
    availableTags: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['save'],
  data() {
    return {
      localConfig: cloneDeep(mergeWithDefaults(this.config)),
      maxMetrics: MAX_METRICS_PER_SECTION,
    };
  },
  computed: {
    baseConfig() {
      return mergeWithDefaults(this.config);
    },
    availableMetrics() {
      // Sorted on the translated label rather than the id, since several labels
      // start with a different word than their key ("Average Handle Time" for
      // handle_time), and so the order stays correct in any locale.
      return METRIC_IDS
        .map(id => ({
          id,
          label: this.$t(`settings_tab.agent_tab.dashboard.pulse_configuration.metrics.${id}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    isDataChanged() {
      return !isEqual(this.localConfig, this.baseConfig);
    },
    availableSegmentsByIndex() {
      return this.localConfig.segments.map((segment, index) => {
        const otherIndex = index === 0 ? 1 : 0;
        const otherSelected = this.localConfig.segments[otherIndex]?.segment_names || [];
        const currentSelected = segment.segment_names || [];
        return difference(this.segments, difference(otherSelected, currentSelected))
          .sort((a, b) => a.localeCompare(b));
      });
    },
  },
  watch: {
    baseConfig: {
      handler(newVal) {
        this.localConfig = cloneDeep(newVal);
      },
      deep: true,
    },
  },
  methods: {
    onTotalMetricsUpdate(metrics) {
      this.localConfig.total_metrics = metrics;
    },

    onSegmentUpdate(index, updatedSegment) {
      this.localConfig.segments[index] = { ...updatedSegment };
    },

    onSave() {
      const cleanedSegments = (this.localConfig.segments || DEFAULT_CONFIG.segments).map(segment => ({
        segment_names: segment.segment_names || [],
        metrics: segment.metrics || [],
      }));

      const normalizeCost = (val) => (typeof val === 'number' && Number.isFinite(val) ? val : null);

      const cleanConfig = {
        total_metrics: this.localConfig.total_metrics || DEFAULT_CONFIG.total_metrics,
        segments: cleanedSegments,
        cost_config: {
          phone_call_cost: normalizeCost(this.localConfig.cost_config?.phone_call_cost),
          agent_chat_cost: normalizeCost(this.localConfig.cost_config?.agent_chat_cost),
          automated_conversation_cost: normalizeCost(this.localConfig.cost_config?.automated_conversation_cost),
        },
        sales_lead_tags: this.localConfig.sales_lead_tags || [],
        include_no_interaction: this.localConfig.include_no_interaction !== false,
      };
      this.$emit('save', cleanConfig);
    },

    onCancel() {
      this.localConfig = cloneDeep(this.baseConfig);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../styles/aiq-variables.scss";

.pulse-config {
  padding: 15px;
  background-color: $aiq-bgc-message-input;
  border-radius: 4px;
}

.config-label {
  font-size: 12px;
  font-weight: 600;
  color: $aiq-color-text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 15px;
}

.total-metrics-section {
  width: 100%;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.title-with-info {
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $aiq-color-text-primary;
  }

  .section-info {
    font-size: 13px;
    color: $aiq-color-text-muted;
    cursor: pointer;
  }
}

.metric-count {
  font-size: 13px;
  color: $aiq-color-text-muted;
  font-weight: 500;
}

.no-interaction-section {
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;

  // aiq-switch reads these off any ancestor, and the library default is blue.
  --aiq-switch-on-color: #30C28B;
  --aiq-switch-off-color: #C0CCDA;

  .section-header {
    margin-bottom: 0;
  }
}

.cost-config-section {
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

.sales-lead-tags-section {
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

.cost-fields {
  display: flex;
  gap: 32px;
}

.cost-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  :deep(.el-input) {
    width: 120px;
  }

  :deep(.el-input-number .el-input__inner) {
    text-align: left;
  }
}

.cost-field-label {
  font-size: 13px;
  font-weight: 500;
  color: $aiq-color-text-primary;
}

.cost-input-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.currency-symbol {
  font-size: 14px;
  font-weight: 500;
  color: $aiq-color-text-primary;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
}
</style>

<style lang="scss">
@import "../../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .pulse-config {
  background-color: $aiq-bgc-sidebar-item-hover;

  .config-label {
    color: $aiq-color-text-muted;
  }

  .title-with-info h3 {
    color: $aiq-color-black;
  }

  .total-metrics-section {
    border-bottom-color: $aiq-bdc-input-default;
  }

  ::v-deep .el-input__inner {
    color: white;
  }

  .metric-count {
    color: white;
  }

  .cost-config-section {
    border-bottom-color: $aiq-bdc-input-default;
  }

  .sales-lead-tags-section {
    border-bottom-color: $aiq-bdc-input-default;
  }

  .cost-field-label {
    color: $aiq-color-black;
  }

  .currency-symbol {
    color: $aiq-color-black;
  }

  .action-buttons {
    border-top-color: $aiq-color-border-dark;
  }
}

.pulse-segment-checkbox-dropdown {
  .el-select-dropdown__item {
    padding: 8px 12px;
    height: auto;
    line-height: normal;

    &.selected::after,
    &.is-selected::after {
      display: none;
      content: none;
    }

    .el-icon {
      display: none;
    }

    .checkbox-option {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;

      .el-checkbox {
        pointer-events: none;
        margin-right: 0;
        height: auto;

        .el-checkbox__input {
          display: flex;
          align-items: center;
        }

        .el-checkbox__label {
          display: none;
        }
      }

      .option-label {
        color: inherit;
        font-size: 14px;
        line-height: 1.4;
      }
    }
  }
}

[data-theme="dark"] {
  .pulse-config-dropdown .el-select-dropdown__item {
    color: white;
  }

  .pulse-segment-checkbox-dropdown .el-select-dropdown__item .checkbox-option .option-label {
    color: white;
  }
}
</style>
